/**
 * Information commands providing details about Ameta's technology, culture, and opportunities.
 *
 * @module terminal/commands/info
 */
import type { CommandResult, CommandContext } from './types';

/**
 * Information commands for company details.
 * Provides access to tech stack, integrations, culture, methodology, and career info.
 *
 * @example
 * ```
 * stack         - Technology stack overview
 * integrations  - Supported protocols and integrations
 * culture       - Company values and principles
 * how           - Development methodology (DISCOVER → DELIVER)
 * blog          - Knowledge base links
 * jobs/careers  - Interactive coding challenge (apply to work at Ameta)
 * challenge     - Interactive coding challenge
 * privacy       - Privacy policy summary
 * ```
 */
const infoCommands: Record<
	string,
	(args: string[], context: CommandContext) => CommandResult | null
> = {
	stack: () => ({
		type: 'ascii',
		content: [
			'╔════════════════════════════════════════════╗',
			'║              TECH STACK                    ║',
			'╠════════════════════════════════════════════╣',
			'║                                            ║',
			'║  Frontend:  Svelte, React, TypeScript      ║',
			'║             Tailwind CSS                   ║',
			'║                                            ║',
			'║  Backend:   Python, FastAPI, Rust           ║',
			'║                                            ║',
			'║  Database:  PostgreSQL, TimescaleDB         ║',
			'║             InfluxDB                       ║',
			'║                                            ║',
			'║  Infra:     Docker, AWS, Railway, Vercel    ║',
			'║                                            ║',
			'║  Protocols: OPC-UA, Modbus, MQTT, REST      ║',
			'║                                            ║',
			'║  We use what works. No hype-driven dev.     ║',
			'╚════════════════════════════════════════════╝'
		].join('\n')
	}),

	integrations: () => ({
		type: 'text',
		lines: [
			'INTEGRATIONS',
			'',
			'Industrial Protocols:',
			'  - OPC-UA (Unified Architecture)',
			'  - Modbus TCP/RTU',
			'  - MQTT / Sparkplug B',
			'  - Profinet / EtherNet/IP',
			'',
			'Cloud & APIs:',
			'  - REST / GraphQL APIs',
			'  - Azure IoT Hub',
			'  - AWS IoT Core',
			'  - Custom webhook integrations',
			'',
			'ERP & Business:',
			'  - SAP integration',
			'  - Microsoft Dynamics',
			'  - Custom ERP connectors'
		]
	}),

	culture: () => ({
		type: 'text',
		lines: [
			'OUR VALUES',
			'',
			'1. Solve real problems - Not everything needs blockchain',
			'2. Ship it - Perfect is the enemy of done',
			'3. Own it - From architecture to deployment',
			'4. Stay curious - Technology never stops evolving',
			'5. Be direct - No corporate BS, no buzzword bingo',
			'',
			'We believe in flat hierarchies, deep work, and',
			'choosing the right tool for the job.'
		]
	}),

	how: () => ({
		type: 'text',
		lines: [
			'OUR METHODOLOGY',
			'',
			'1. DISCOVER  - Understand the actual problem',
			'2. PROTOTYPE - Build fast, learn faster',
			'3. VALIDATE  - Test with real data',
			'4. DELIVER   - Ship production-ready solutions',
			'5. ITERATE   - Continuous improvement',
			'',
			"We don't do 6-month spec documents.",
			'We ship working software in weeks, not months.'
		]
	}),

	blog: () => ({
		type: 'text',
		lines: [
			'KNOWLEDGE BASE',
			'',
			'Read our articles on:',
			'  - Industrial IoT best practices',
			'  - Predictive maintenance with ML',
			'  - OPC-UA implementation guides',
			'  - Production monitoring strategies',
			'',
			'Visit: /blog'
		]
	}),

	jobs: () => ({ type: 'game', game: 'challenge' }),

	careers: () => ({ type: 'game', game: 'challenge' }),

	challenge: () => ({ type: 'game', game: 'challenge' }),

	privacy: () => ({
		type: 'text',
		lines: [
			'PRIVACY',
			'',
			'We take data privacy seriously:',
			'  - No tracking cookies (besides essential)',
			'  - No third-party analytics',
			'  - GDPR compliant',
			'  - Data stored in EU',
			'',
			'Questions? hello@ameta.no'
		]
	})
};

export { infoCommands };
